import React, { useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Col, Row, Spinner } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Table from 'react-bootstrap/Table';
import { AppContext } from '../../App';
import LabDataLayout, { useLabDataFilters } from '../../components/LabData/LabDataLayout';
import LabDataKpiCard from '../../components/LabData/LabDataKpiCard';
import { getBudgetDetail } from '../../clients/lab_data_client';
import {
  formatNisAmount,
  formatPercentUsed,
  getBurnProgressVariant,
} from '../../utils/labData';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';

const LOG_CONTEXT = 'BudgetDetailPage';

/**
 * Top-10 breakdown table (supplier or project) inside a glass panel.
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {Array} [props.rows]
 * @param {string} props.nameKey
 * @param {string} props.amountKey
 */
const BreakdownTable = ({ title, rows, nameKey, amountKey }) => (
  <div className="glass-panel lab-data-table-card h-100">
    <h3 className="lab-data-section-title">{title}</h3>
    {!rows?.length ? (
      <div className="glass-empty">No data.</div>
    ) : (
      <div className="glass-table-wrap">
        <Table hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th className="text-end">Spend</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.id ?? i}>
                <td>{row[nameKey] ?? row.name ?? '—'}</td>
                <td className="text-end">
                  {formatNisAmount(row[amountKey] ?? row.total_nis ?? row.spent_nis)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )}
  </div>
);

/**
 * One budget's burn summary and the top supplier and project breakdowns.
 */
const BudgetDetailContent = () => {
  const { id } = useParams();
  const { token } = useContext(AppContext);
  const filters = useLabDataFilters();
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!token || !id || !filters) return;
    setLoading(true);
    logInfo(LOG_CONTEXT, 'load budget detail', { id, from: filters.from, to: filters.to });
    getBudgetDetail(token, id, { from: filters.from, to: filters.to }).then(res => {
      setLoading(false);
      if (!res.success) {
        showToast(res.messages?.[0] || 'Failed to load budget detail', 'error', 'top-right', 5000);
        return;
      }
      setDetail(res.data);
    });
  }, [token, id, filters]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading budget detail</span>
        </Spinner>
      </div>
    );
  }

  const line = detail?.data?.budget_line ?? detail?.budget_line ?? detail?.data ?? {};
  const bySupplier = detail?.data?.by_supplier ?? detail?.by_supplier ?? [];
  const byProject = detail?.data?.by_project ?? detail?.by_project ?? [];
  const pct = Number(line.percent_used ?? 0);
  const numberLabel = line.number || line.code || '';
  const pi = line.primary_investigator;
  const piLabel = pi
    ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim() || pi.username || '—'
    : '—';
  const relatedUsers = Array.isArray(line.related_users) ? line.related_users : [];

  return (
    <>
      <Link className="lab-data-back-link" to="/lab-data/budgets">
        ← Back to budgets
      </Link>
      <h3 className="lab-data-detail-title">
        {numberLabel}
        {line.name ? ` — ${line.name}` : ''}
      </h3>
      <p className="lab-data-muted">Primary investigator: {piLabel}</p>
      {relatedUsers.length ? (
        <p className="lab-data-muted">
          Related users:{' '}
          {relatedUsers
            .map(
              u =>
                `${u.first_name || ''} ${u.last_name || ''}`.trim() ||
                u.username ||
                `User ${u.id}`
            )
            .join(', ')}
        </p>
      ) : null}
      <Row className="g-3 lab-data-kpi-grid mt-1">
        <Col xs={12} sm={6} lg={3}>
          <LabDataKpiCard label="Allocated" value={formatNisAmount(line.allocated_amount)} />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <LabDataKpiCard label="Spent" value={formatNisAmount(line.spent_nis)} />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <LabDataKpiCard label="Remaining" value={formatNisAmount(line.remaining_nis)} />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <LabDataKpiCard label="Used" value={formatPercentUsed(pct)} />
        </Col>
      </Row>
      <ProgressBar
        className="lab-data-burn-progress lab-data-burn-progress--detail"
        variant={getBurnProgressVariant(pct)}
        now={Math.min(100, Math.max(0, pct))}
      />
      <Row className="g-3">
        <Col xs={12} md={6}>
          <BreakdownTable
            title="By supplier (top 10)"
            rows={bySupplier}
            nameKey="supplier_name"
            amountKey="spent_nis"
          />
        </Col>
        <Col xs={12} md={6}>
          <BreakdownTable
            title="By project (top 10)"
            rows={byProject}
            nameKey="project_name"
            amountKey="spent_nis"
          />
        </Col>
      </Row>
    </>
  );
};

/**
 * Budget burn detail. The date range still comes from the shared Lab Data layout.
 */
const BudgetDetailPage = () => (
  <LabDataLayout title="Budget detail">
    <BudgetDetailContent />
  </LabDataLayout>
);

export default BudgetDetailPage;
